import type { SuggestedConnectionAxis } from '../suggested-connection-reason';

/**
 * WHAT A SERIES ITEM IS (#4215). Most onboarding-inbox rows are not tasks — "show this on
 * day N" — so they ride the bounty row (day, on/off, channels, copy, optional points) with
 * a different `kind`:
 *
 * - `task`        — a bounty: do the step, earn the points. Wears 🎯.
 * - `message`     — a line, a body and a button. Its step supplies the words and the link;
 *                   opening the button is the click that completes it (and pays, if it has
 *                   points). Wears its own emoji.
 * - `suggestions` — artist cards for ONE match axis, picked when it is delivered. Pays only
 *                   on a real Connect or Follow from the card.
 */
export const BOUNTY_KINDS = ['task', 'message', 'suggestions'] as const;

export type BountyKind = (typeof BOUNTY_KINDS)[number];

export function isBountyKind(value: unknown): value is BountyKind {
  return typeof value === 'string' && (BOUNTY_KINDS as readonly string[]).includes(value);
}

/** The step behind each suggestion item, and the axis it shows. */
export const SUGGESTION_STEP_AXES: Readonly<Record<string, Exclude<SuggestedConnectionAxis, 'overall'>>> = {
  coffeeMatches: 'coffee',
  visualMatches: 'visual',
  identityMatches: 'identity',
};

export function suggestionAxisFor(stepCode: string): Exclude<SuggestedConnectionAxis, 'overall'> | null {
  return SUGGESTION_STEP_AXES[stepCode] ?? null;
}

/**
 * Steps whose link is picked when the item is delivered — the current announcement, the
 * thread that took off, the reward a welcome gift unlocks — rather than fixed in the catalog. With nothing worth showing the
 * item waits rather than sending an empty line.
 */
export const DYNAMIC_MESSAGE_STEP_CODES: readonly string[] = ['platformHighlight', 'hotThread', 'welcomeRewards'];

/**
 * The day-0 welcome gift (#4213): its reward and unlocked count are read for each member, so
 * whether it has anything to say is only known once the member is.
 */
export const WELCOME_GIFT_STEP_CODE = 'welcomeRewards';

/**
 * A step that only makes sense as a series item — artist cards, a thread picked on delivery,
 * the welcome gift — so neither "All steps" (whose switch makes a task out of a step) nor the
 * quest step picker offers it.
 */
export function isSeriesItemStep(stepCode: string): boolean {
  return suggestionAxisFor(stepCode) !== null || DYNAMIC_MESSAGE_STEP_CODES.includes(stepCode);
}

/** How many comments a thread needs before "everyone's talking about it" is true. */
export const HOT_THREAD_DEFAULT_MIN_REPLIES = 25;

/** Fewer cards than this and a suggestion item is skipped rather than sent half-empty. */
export const SUGGESTION_MIN_CARDS = 2;

/** How many cards a suggestion item shows. */
export const SUGGESTION_CARD_COUNT = 4;

/** A task is something she does; a step only ever shown to her in the series is not one. */
function taskViolation(code: string | null, axis: SuggestedConnectionAxis | null): string | null {
  if (axis) return 'An artist-suggestion step is shown as cards, not done as a task — make it a Suggestions item.';
  if (code && DYNAMIC_MESSAGE_STEP_CODES.includes(code)) {
    return 'This step is picked for each member as it is delivered, not done as a task — make it a Message item.';
  }
  return null;
}

/**
 * Why this step cannot back an item of this kind, or null when it can. Shared so the
 * admin never offers what the server refuses.
 */
export function kindStepViolation(
  kind: BountyKind,
  step: { code: string; completion: 'measured' | 'click' } | null | undefined,
  inSeries: boolean
): string | null {
  const axis = step ? suggestionAxisFor(step.code) : null;
  if (kind === 'task') return taskViolation(step?.code ?? null, axis);
  if (!inSeries) return 'Messages and artist suggestions belong to the onboarding series — give it a day.';
  if (!step) return 'There is no step with that code.';
  if (kind === 'suggestions') return axis ? null : 'A suggestions item needs one of the artist-match steps.';
  if (axis) return 'An artist-match step is shown as cards — make it a Suggestions item.';
  if (step.completion !== 'click') return 'A message completes when it is opened, so it needs a click step.';
  return null;
}

/**
 * Member-written text about to be dropped inside a quoted series label
 * (`Everyone’s talking about “{title}”`).
 *
 * Strips ASCII controls, line and paragraph breaks, and bidi overrides so the
 * title cannot become a second line or reverse the sentence, and strips the
 * quotation marks that close that label (U+201C, U+201D, and ASCII quotes) so
 * the title cannot end the quotation and continue as ArtHelper's own words.
 * The same string is what the digest line, the subject, and the series-email
 * heading read.
 */
const QUOTED_TITLE_CONTROLS = /[\u0000-\u001F\u007F\u2028\u2029]/g;
const QUOTED_TITLE_BIDI = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const QUOTED_TITLE_MARKS = /["'\u201C\u201D]/g;

export function neutralizeQuotedTitle(value: string): string {
  return value
    .replace(QUOTED_TITLE_CONTROLS, ' ')
    .replace(QUOTED_TITLE_BIDI, '')
    .replace(QUOTED_TITLE_MARKS, '')
    .replace(/[ \t\f\v]+/g, ' ')
    .trim();
}

/** `{title}`-style holes in a label, filled from what a dynamic source found. */
export function fillTemplate(text: string, vars: Readonly<Record<string, string | number>> | null | undefined): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (hole, key: string) => (key in vars ? String(vars[key]) : hole));
}
